import { zodResolver } from "@hookform/resolvers/zod";
import { App, Button, Form, Input } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { adminApi } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type LoginVals = z.infer<typeof LoginSchema>;

export function LoginPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSessionFromLogin);

  useEffect(() => {
    const reason = (location.state as { reason?: string } | null)?.reason;
    if (reason === "idle") {
      message.warning(t("login.idleLogout"));
    }
  }, [location.state, message, t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginVals>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: "", password: "" },
  });

  const login = useMutation({
    mutationFn: async (values: LoginVals) => {
      const { data, response } = await adminApi.POST("/admin/v1/auth/login", {
        body: { email: values.email, password: values.password },
      });
      if (
        data?.access_token &&
        data?.refresh_token &&
        data.token_type === "Bearer"
      ) {
        return data;
      }
      throw new Error(`login_${response.status}`);
    },
    onSuccess: async (pair) => {
      setSession(pair.access_token, pair.refresh_token);
      qc.invalidateQueries({ queryKey: ["admin"] });
      const me = await adminApi.GET("/admin/v1/me");
      if (!me.data) {
        message.error(t("login.failed"));
        return;
      }
      message.success("OK");
      if (!me.data.mfa_bound) {
        navigate("/mfa-setup", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    },
    onError: () => message.error(t("login.failed")),
  });

  const onFinish = handleSubmit(async (vals) => {
    await login.mutateAsync(vals);
  });

  const site = import.meta.env.VITE_PUBLIC_SITE_ORIGIN?.replace(/\/$/, "");

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{t("login.title")}</div>
          <div style={{ color: "#888", marginTop: 8 }}>{t("brand.slogan")}</div>
        </div>
        <Form layout="vertical" onFinish={onFinish} requiredMark="optional">
          <Form.Item
            label={t("login.email")}
            validateStatus={errors.email ? "error" : undefined}
            help={errors.email?.message}
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input {...field} autoComplete="username" type="email" />
              )}
            />
          </Form.Item>
          <Form.Item
            label={t("login.password")}
            validateStatus={errors.password ? "error" : undefined}
            help={errors.password?.message}
          >
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Input.Password {...field} autoComplete="current-password" />
              )}
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={login.isPending}
          >
            {t("login.submit")}
          </Button>
        </Form>
        {site ? (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <a href={`${site}/`} target="_blank" rel="noreferrer">
              {t("login.officialSite")}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
